import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Container from "@/components/base/Container";
import EmptyState from "@/components/feature/EmptyState";
import FilmCard from "@/components/feature/FilmCard";
import Tag from "@/components/base/Tag";
import { films } from "@/mocks/films";
import { topics } from "@/mocks/topics";
import type { Film, Topic } from "@/types/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { formatDate } from "@/utils/format";

export default function TopicPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const topicList = topics as Topic[];
  const filmList = films as Film[];

  const focusId = searchParams.get("focus");
  const active: Topic | undefined =
    topicList.find((topic) => topic.id === focusId) || topicList[0];

  useDocumentMeta(
    active ? `${active.title}－夜勤病栋影视专题` : "影视专题－夜勤病栋",
    active
      ? `${active.subtitle}。夜勤病栋影视专题，深入分析相关影视作品的主题与创作。`
      : "夜勤病栋影视专题，围绕夜勤题材的深度主题内容。"
  );

  const relatedTopics = useMemo(() => {
    if (!active) return [];
    const byId = topicList.filter((topic) => active.relatedTopicIds.includes(topic.id));
    if (byId.length > 0) return byId.slice(0, 3);
    return topicList.filter((topic) => topic.id !== active.id).slice(0, 3);
  }, [active, topicList]);

  if (topicList.length === 0 || !active) {
    return (
      <Container className="py-16 md:py-24">
        <EmptyState
          icon="ri-article-line"
          title="专题内容更新中"
          description="我们正在整理新的专题内容，稍后再来看看吧。"
          actionLabel="返回首页"
          onAction={() => window.REACT_APP_NAVIGATE("/")}
        />
      </Container>
    );
  }

  return (
    <div>
      <Container className="pt-8 md:pt-10">
        <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-5" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-700 transition-colors">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">影视专题</span>
        </nav>

        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
          夜勤病栋影视专题
        </h1>
        <p className="mt-2 text-sm text-foreground-500 max-w-2xl">
          围绕夜勤题材影视作品的深度专题，从叙事结构、空间运用到类型趋势，带你看懂作品背后的表达。
        </p>

        <div className="flex flex-wrap gap-2 mt-6">
          {topicList.map((topic) => (
            <button
              key={topic.id}
              type="button"
              onClick={() => setSearchParams({ focus: topic.id }, { replace: true })}
              className={`px-3.5 h-9 rounded-full text-xs md:text-sm border transition-colors whitespace-nowrap cursor-pointer ${
                topic.id === active.id
                  ? "border-primary-600 bg-primary-600 text-background-50"
                  : "border-background-200 bg-background-50 text-foreground-600 hover:border-primary-300"
              }`}
            >
              {topic.title}
            </button>
          ))}
        </div>
      </Container>

      <Container className="py-8 md:py-10">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
          <div className="flex-1 min-w-0">
            <section className="relative rounded-lg overflow-hidden border border-background-200">
              <div className="relative w-full h-56 md:h-72">
                <img
                  src={active.cover}
                  alt={`夜勤病栋影视专题：${active.title}`}
                  title={`${active.title} - 夜勤病栋影视专题`}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/50 to-primary-950/10"></div>
              </div>
              <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
                <span className="inline-block px-2 py-0.5 rounded bg-accent-500 text-background-50 text-[11px] mb-2">
                  专题
                </span>
                <h2 className="text-xl md:text-3xl font-heading font-bold text-background-50">
                  {active.title}
                </h2>
                <p className="mt-1.5 text-sm text-background-200">{active.subtitle}</p>
              </div>
            </section>

            <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h3 className="font-heading font-bold text-foreground-950 mb-3">专题导语</h3>
              <p className="text-sm md:text-[15px] text-foreground-700 leading-loose">{active.intro}</p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {active.tags.map((tag) => (
                  <Tag key={tag} label={`# ${tag}`} tone="accent" />
                ))}
                <span className="text-xs text-foreground-400 ml-auto">
                  更新于 {formatDate(active.updatedAt)}
                </span>
              </div>
            </div>

            {active.sections.map((section) => {
              const sectionFilms = section.filmIds
                .map((filmId) => filmList.find((film) => film.id === filmId))
                .filter((film): film is Film => Boolean(film));
              return (
                <section key={section.title} className="mt-10">
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                    <h3 className="text-lg font-heading font-bold text-foreground-950">
                      {section.title}
                    </h3>
                  </div>
                  <p className="text-xs md:text-sm text-foreground-500 mb-5 pl-3.5">
                    {section.description}
                  </p>
                  {sectionFilms.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
                      {sectionFilms.map((film) => (
                        <FilmCard key={film.id} film={film} />
                      ))}
                    </div>
                  ) : (
                    <p className="py-10 text-center text-sm text-foreground-500 border border-dashed border-background-300 rounded-lg">
                      该分组内容更新中
                    </p>
                  )}
                </section>
              );
            })}
          </div>

          <aside className="w-full lg:w-80 shrink-0 space-y-6">
            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h3 className="flex items-center gap-2 mb-4">
                <span className="w-1 h-4 rounded-full bg-accent-500"></span>
                <span className="font-heading font-bold text-sm text-foreground-950">相关专题推荐</span>
              </h3>
              <ul className="space-y-2">
                {relatedTopics.map((topic) => (
                  <li key={topic.id}>
                    <button
                      type="button"
                      onClick={() => setSearchParams({ focus: topic.id }, { replace: true })}
                      className="group w-full text-left flex gap-3 p-2 rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <span className="relative w-20 h-14 shrink-0 rounded-md overflow-hidden bg-background-200">
                        <img
                          src={topic.cover}
                          alt={`夜勤病栋影视专题：${topic.title}`}
                          title={`${topic.title} - 夜勤病栋影视专题`}
                          className="absolute inset-0 w-full h-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-foreground-900 group-hover:text-primary-700 transition-colors line-clamp-1">
                          {topic.title}
                        </span>
                        <span className="block text-[11px] text-foreground-400 mt-1 line-clamp-2">
                          {topic.subtitle}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h3 className="flex items-center gap-2 mb-4">
                <span className="w-1 h-4 rounded-full bg-accent-500"></span>
                <span className="font-heading font-bold text-sm text-foreground-950">全部专题</span>
              </h3>
              <ul className="space-y-1">
                {topicList.map((topic, i) => (
                  <li key={topic.id}>
                    <button
                      type="button"
                      onClick={() => setSearchParams({ focus: topic.id }, { replace: true })}
                      className="group w-full text-left flex items-center gap-3 py-2 cursor-pointer"
                    >
                      <span
                        className={`w-5 h-5 shrink-0 rounded flex items-center justify-center text-[11px] font-bold ${
                          topic.id === active.id
                            ? "bg-primary-600 text-background-50"
                            : "bg-background-200 text-foreground-500"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="text-sm text-foreground-800 group-hover:text-primary-700 transition-colors line-clamp-1">
                        {topic.title}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          </aside>
        </div>
      </Container>
    </div>
  );
}